import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { useEffect, useState } from 'react'
import { decodeToken } from 'react-jwt'
import { loginRequest } from '@/auth-config'
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser'

export type UserRole = 'AllAccessUser' | 'WoolStoreUser'

export function extractRoles(decodedToken: any): UserRole[] {
  // We are expecting the claim containing roles to be "roles"
  const claim = decodedToken.roles
  // We are expecting that the roles claim is an array of strings
  if (Array.isArray(claim))
    // Filter out values that are not in UserRoles
    return claim.filter((r) => r === 'AllAccessUser' || r === 'WoolStoreUser')
  return []
}

export function useUserRoles(): {
  roles: UserRole[] | null
} {
  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const [roles, setRoles] = useState<UserRole[] | null>(null)

  useEffect(() => {
    const getToken = async () => {
      const result = await instance.acquireTokenSilent(loginRequest).catch(async (error) => {
        // If silent token acquisition fails, try to acquire a token interactively
        if (error instanceof InteractionRequiredAuthError) {
          return instance.acquireTokenRedirect(loginRequest)
        }

        // TODO: any other errors should get reported via Elastic APM
        throw error
      })

      if (result) {
        // Decode the token to access the claims
        const decodedToken = decodeToken(result.accessToken)

        // Extract the roles from the token
        const roles: UserRole[] = extractRoles(decodedToken)
        setRoles(roles)
      }
    }

    if (isAuthenticated && inProgress === InteractionStatus.None) {
      getToken()
    }
  }, [isAuthenticated, inProgress])

  return { roles }
}
